import * as z from "zod";
import {
  DEFAULT_WAREHOUSE_GRADE_NAMES,
  type GradeMapping,
  GradeMappingSchema,
  resolveWarehouseGradeNames,
} from "./grade-mapping";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
} from "./grouped-settings-changes";
import type { GradeBinMapping } from "./redo-managed-returns-settings";

const GradeBinMappingSchema = z.object({
  gradeName: z.string(),
  storageLocationId: z.string(),
});

export const ReturnsWarehouseSettingsSchema = z.object({
  grades: z.object({
    customGradesEnabled: z.boolean(),
    gradeOptions: z.array(z.string()),
    outcomeOptions: z.array(z.string()),
    gradeMappings: z.array(GradeMappingSchema),
  }),
  bins: z.object({ gradeBinMappings: z.array(GradeBinMappingSchema) }),
  processing: z.object({ requireImageForUndeclaredItems: z.boolean() }),
});

export type ReturnsWarehouseSettings = z.infer<
  typeof ReturnsWarehouseSettingsSchema
>;

const settingsShape = ReturnsWarehouseSettingsSchema.shape;

/** Only the fields a merchant changed. Grade actions skip the team patch: the handler saves them through the managed-returns grade settings. */
export const ReturnsWarehouseSettingsChangesSchema = z.object({
  grades: settingsShape.grades.partial().optional(),
  bins: settingsShape.bins.partial().optional(),
  processing: settingsShape.processing.partial().optional(),
});

export type ReturnsWarehouseSettingsChanges = z.infer<
  typeof ReturnsWarehouseSettingsChangesSchema
>;

/** The slice of `team.settings` the Warehouse tab reads. Structurally matches both the client team and the server's raw document. */
export interface ReturnsWarehouseSettingsSource {
  returnItemDisposition?: {
    enabled?: boolean | null;
    gradeOptions?: string[] | null;
    outcomeOptions?: string[] | null;
  } | null;
  redoManagedReturns?: {
    gradeMappings?: GradeMapping[] | null;
    gradeBinMappings?: GradeBinMapping[] | null;
  } | null;
  returns?: { requireImageForUndeclaredItems?: boolean | null } | null;
}

export function returnsWarehouseSettingsChanges(
  initial: ReturnsWarehouseSettings,
  next: ReturnsWarehouseSettings,
): ReturnsWarehouseSettingsChanges {
  return ReturnsWarehouseSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

export function returnsWarehouseSettingsFromTeam(
  settings: ReturnsWarehouseSettingsSource,
): ReturnsWarehouseSettings {
  return {
    grades: {
      customGradesEnabled: !!settings.returnItemDisposition?.enabled,
      gradeOptions: settings.returnItemDisposition?.gradeOptions ?? [],
      outcomeOptions: settings.returnItemDisposition?.outcomeOptions ?? [],
      gradeMappings: settings.redoManagedReturns?.gradeMappings ?? [],
    },
    bins: {
      gradeBinMappings: settings.redoManagedReturns?.gradeBinMappings ?? [],
    },
    processing: {
      requireImageForUndeclaredItems:
        settings.returns?.requireImageForUndeclaredItems ?? true,
    },
  };
}

type GradeOptionsChoice = Pick<
  ReturnsWarehouseSettings["grades"],
  "customGradesEnabled" | "gradeOptions"
>;

/** Turning custom grades on with none defined starts from the default A–E grades. */
export function customGradeOptions({
  customGradesEnabled,
  gradeOptions,
}: GradeOptionsChoice): string[] {
  return customGradesEnabled && gradeOptions.length === 0
    ? [...DEFAULT_WAREHOUSE_GRADE_NAMES]
    : gradeOptions;
}

/** The grades the warehouse assigns once these grade options are saved. */
export function warehouseGradeNames(grades: GradeOptionsChoice): string[] {
  return resolveWarehouseGradeNames({
    customGradesEnabled: grades.customGradesEnabled,
    gradeOptions: customGradeOptions(grades),
  });
}

function returnsWarehouseSettingsToTeamPatch(
  settings: ReturnsWarehouseSettingsSource,
  value: ReturnsWarehouseSettings,
): { set: Record<string, unknown>; unset: string[] } {
  const { customGradesEnabled, outcomeOptions } = value.grades;
  const gradeOptions = customGradeOptions(value.grades);
  const set: Record<string, unknown> = settings.returnItemDisposition
    ? {
        "returnItemDisposition.enabled": customGradesEnabled,
        "returnItemDisposition.gradeOptions": gradeOptions,
        "returnItemDisposition.outcomeOptions": outcomeOptions,
      }
    : {
        returnItemDisposition: {
          enabled: customGradesEnabled,
          gradeOptions,
          outcomeOptions,
        },
      };
  set["returns.requireImageForUndeclaredItems"] =
    value.processing.requireImageForUndeclaredItems;
  // Bins belong to managed returns; a team without it has nowhere to keep them.
  // Bins for grades outside the saved set are kept, so toggling custom grades back restores them.
  if (settings.redoManagedReturns) {
    set["redoManagedReturns.gradeBinMappings"] = value.bins.gradeBinMappings;
  }
  return { set, unset: [] };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere.
 */
export function returnsWarehouseTeamPatchForChanges(
  settings: ReturnsWarehouseSettingsSource,
  changes: ReturnsWarehouseSettingsChanges,
): { set: Record<string, unknown>; unset: string[] } {
  const storedValue = returnsWarehouseSettingsFromTeam(settings);
  const next: ReturnsWarehouseSettings = {
    grades: { ...storedValue.grades, ...changes.grades },
    bins: { ...storedValue.bins, ...changes.bins },
    processing: { ...storedValue.processing, ...changes.processing },
  };
  return changedTeamPatchPaths(
    returnsWarehouseSettingsToTeamPatch(settings, storedValue),
    returnsWarehouseSettingsToTeamPatch(settings, next),
  );
}
